import { type Actor, type Anchor, type PageRect } from '@shared/board'
import { createContext, use } from 'react'
import { type SessionsClient } from '@/session/store'
import { type BoardClient } from './client'
import { type Rect } from '@shared/whiteboard/geometry'
import { type EngineApi } from './engine/types'

/** Where the mounted canvas engine registers itself, for the chat to use. */
export class EngineSlot {
  current: EngineApi | null = null
  private waiting: ((api: EngineApi) => void)[] = []
  set(api: EngineApi | null) {
    this.current = api
    if (!api) return
    const waiting = this.waiting
    this.waiting = []
    for (const fn of waiting) fn(api)
  }
  /** The engine now, or once a canvas mounts (e.g. a side canvas sliding in). */
  ready(): Promise<EngineApi> {
    if (this.current) return Promise.resolve(this.current)
    return new Promise((resolve) => this.waiting.push(resolve))
  }
}

/** How the chat brings the board into view, whatever the layout. */
export class CanvasControl {
  private show: (() => void) | null = null
  private readonly engine: EngineSlot
  constructor(engine: EngineSlot) {
    this.engine = engine
  }
  /** Set by the page: what showing the canvas means in the current layout. */
  onReveal(fn: (() => void) | null) {
    this.show = fn
  }
  /**
   * Make the canvas visible (in chat-main, slide the side canvas in) and
   * resolve with its engine once it is mounted and its first view is in place.
   */
  async reveal(): Promise<EngineApi> {
    this.show?.()
    const api = await this.engine.ready()
    await api.settled()
    return api
  }
}

/** One thing a change view marks on the canvas (DESIGN.md 信任層). */
export type OverlayMark = {
  id: string
  status: 'added' | 'changed' | 'removed'
  /** Board coordinates: where it is, or was for a removed thing (drawn as a ghost). */
  rect: Rect
  /** For ghosts: what it was. */
  label: string
  /** The step a replay is on: stronger than the rest. */
  current: boolean
}

/**
 * What the canvas draws over the board for the change view: marks around
 * changed things and ghosts of removed ones. Presentation only; nothing here
 * touches the board.
 */
export class BoardOverlay {
  private marks: readonly OverlayMark[] = []
  private readonly listeners = new Set<() => void>()
  subscribe = (fn: () => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }
  getSnapshot = () => this.marks
  set(marks: readonly OverlayMark[]) {
    this.marks = marks
    for (const fn of this.listeners) fn()
  }
}

/** What item views can ask of the board shell (which owns the composer and selection). */
export type BoardActions = {
  /** Open the composer for a message about `anchor`; `nearY` puts it beside a quoted line. */
  sayAbout: (anchor: Anchor, nearY?: number) => void
  /** Open the composer for a reply in this message's thread (unfolding it). */
  reply: (messageId: string) => void
  /** Fold or unfold the thread this message starts. */
  toggleThread: (rootId: string) => void
  /** A new card with the message's words, connected back to it. */
  cardFromMessage: (messageId: string) => void
  /** Move the camera to these items and emphasise them. */
  showItems: (ids: string[]) => void
}

/** Where the board shell registers its actions once mounted. */
export class ActionSlot {
  current: BoardActions | null = null
  set(a: BoardActions | null) {
    this.current = a
  }
}

/** Where the reader should be, and what to point out there. `n` changes on every request. */
export type ReaderTarget = {
  materialId: string
  page: number
  /** A region to point out (fractions of the page). */
  rect: PageRect | null
  n: number
}

/**
 * The reader (MATERIALS.md): which material is open beside the board and
 * where. The page decides where the reader is shown in each layout.
 */
export class ReaderControl {
  private target: ReaderTarget | null = null
  private readonly listeners = new Set<() => void>()
  subscribe = (fn: () => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }
  getSnapshot = () => this.target
  open(materialId: string, page = 1, rect: PageRect | null = null) {
    this.target = { materialId, page, rect, n: (this.target?.n ?? 0) + 1 }
    for (const fn of this.listeners) fn()
  }
  close() {
    this.target = null
    for (const fn of this.listeners) fn()
  }
}

export type BoardCtx = {
  boardId: string
  client: BoardClient
  me: Actor
  /** Card ids that should take focus once their editor mounts. */
  autofocus: Set<string>
  /** Initial plain text for cards created from a paste, consumed on mount. */
  initialText: Map<string, string>
  /** The canvas engine, once mounted: lets the sidebar move the camera. */
  engine: EngineSlot
  actions: ActionSlot
  sessions: SessionsClient
  canvas: CanvasControl
  overlay: BoardOverlay
  reader: ReaderControl
}

/**
 * Which group title is being edited. Owned by the board shell rather than the
 * group view, so it survives the view remounting (StrictMode, engine updates).
 */
export type TitleEditing = { id: string | null; set: (id: string | null) => void }
export const TitleEditingContext = createContext<TitleEditing>({ id: null, set: () => undefined })

export function useTitleEditing(): TitleEditing {
  return use(TitleEditingContext)
}

export const BoardContext = createContext<BoardCtx | null>(null)

export function useBoardCtx(): BoardCtx {
  const v = use(BoardContext)
  if (!v) throw new Error('useBoardCtx outside BoardContext')
  return v
}
